/**
 * Centralized intent-based experience search for RealityCheck.
 *
 * Architecture is backend-agnostic: swap `options.backend` later for
 * vector embeddings, Algolia, Typesense, Meilisearch, or OpenSearch
 * without changing UI call sites.
 */

import { DEFAULT_MIN_SCORE } from './searchConstants';
import { buildSearchDocument, buildSearchIndex } from './buildSearchDocument';
import { parseSearchQuery } from './queryParser';
import { rankSearchResults } from './rankExperiences';

export { SEARCH_PLACEHOLDER, SEARCH_EMPTY_MESSAGE, DEFAULT_MIN_SCORE } from './searchConstants';
export { buildSearchDocument, buildSearchIndex } from './buildSearchDocument';
export { parseSearchQuery } from './queryParser';

/**
 * @typedef {object} SearchDocument
 * @property {string} id
 * @property {object} experience
 * @property {string} titleLower
 * @property {string} haystack
 * @property {object} fields
 * @property {string[]} accessDevices
 * @property {string} priceType
 * @property {number|null} minPrice
 * @property {boolean} isFree
 * @property {boolean} isHeadsetOnly
 * @property {boolean} hasMobile
 * @property {boolean} hasHeadset
 * @property {boolean} hasDesktop
 * @property {string} category
 * @property {string} contentType
 * @property {string[]} themes
 * @property {string[]} bestFor
 * @property {string[]} groupSize
 * @property {string} locationHaystack
 * @property {string} comfortRating
 */

/**
 * @typedef {object} ParsedQuery
 * @property {string} raw
 * @property {string} normalized
 * @property {string} intentCore
 * @property {string[]} tokens
 * @property {string[]} expandedTerms
 * @property {object} constraints
 * @property {string[]} categoryHints
 * @property {string[]} contentTypeHints
 */

/**
 * @typedef {object} SearchResult
 * @property {object} experience
 * @property {number} score
 * @property {string[]} reasons
 */

/**
 * @typedef {object} SearchBackend
 * @property {(experiences: object[], query: string, options?: object) => SearchResult[]} search
 */

/** Default in-browser lexical + intent search backend. */
export const lexicalSearchBackend = {
  search(experiences, query, options = {}) {
    const parsed = parseSearchQuery(query);
    if (!parsed.normalized) {
      return experiences.map((experience) => ({ experience, score: 0, reasons: [] }));
    }

    const index = options.searchIndex || buildSearchIndex(experiences);
    const docs = experiences
      .map((exp) => index.get(exp.id) || buildSearchDocument(exp))
      .filter(Boolean);

    const minScore = options.minScore ?? DEFAULT_MIN_SCORE;
    const ranked = rankSearchResults(docs, parsed, { minScore });

    return ranked.map(({ doc, score, reasons }) => ({
      experience: doc.experience,
      score,
      reasons
    }));
  }
};

/**
 * Search experiences with intent-based ranking.
 *
 * @param {object[]} experiences - candidate pool (already category/location filtered)
 * @param {string} query - natural language or keyword query
 * @param {object} [options]
 * @param {Map} [options.searchIndex] - pre-built index for performance
 * @param {number} [options.minScore] - minimum relevance threshold
 * @param {SearchBackend} [options.backend] - pluggable search backend
 * @returns {object[]} experiences sorted by relevance (best first)
 */
export function searchExperiences(experiences, query, options = {}) {
  const backend = options.backend || lexicalSearchBackend;
  const results = backend.search(experiences, query, options);
  return results.map((r) => r.experience);
}

/**
 * Full search with scores — useful for debugging and future analytics.
 */
export function searchExperiencesWithScores(experiences, query, options = {}) {
  const backend = options.backend || lexicalSearchBackend;
  return backend.search(experiences, query, options);
}

/**
 * Returns true if query is non-empty.
 */
export function hasSearchQuery(query) {
  return Boolean(String(query || '').trim());
}
